import { Modal, Text, Group, Stack, TextInput, Button, Box, DateInput, Divider } from 'components'
import { IconSparkles, IconCheck, IconX } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { useContext, useCallback, useState, useEffect } from 'react'
import { StepperContext } from '../../context/StepperContext'
import { AILessonsPlanningModalProps } from './interfaces'
import { useForm, notifications } from 'context'
import { useLessonPlanningGenerator } from '../../hooks/useLessonPlanningGenerator'
import { AILoadingState } from '../AIGeneratorModal/components/AILoadingState'
import { RegenerateMeetingsConfirmModal } from './RegenerateMeetingsConfirmModal'
import { deleteAllMeetings, getMeetingsCount } from '../../utils/meetingDeletionHelper'
import dayjs from 'dayjs'

interface FormValues {
  startDate: Date | string
  durationWeeks: number | string
}

export function AILessonsPlanningModal(props: AILessonsPlanningModalProps) {
  const { opened, onClose, projectId, onGenerate } = props
  const { t, i18n } = useTranslation()
  const { setActive } = useContext(StepperContext)
  const { generate, isGenerating, progress } = useLessonPlanningGenerator()

  const [confirmModalOpened, setConfirmModalOpened] = useState(false)
  const [meetingsCount, setMeetingsCount] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)

  const dateValueFormat = i18n.language === 'pt-BR' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'

  useEffect(() => {
    if (opened && projectId) {
      checkExistingMeetings()
    }
  }, [opened, projectId])

  const checkExistingMeetings = async () => {
    if (!projectId) return

    try {
      const count = await getMeetingsCount(projectId)
      setMeetingsCount(count)
    } catch (error) {
      console.error('Error checking meetings:', error)
    }
  }

  const form = useForm<FormValues>({
    initialValues: {
      startDate: new Date(),
      durationWeeks: 1,
    },
    validate: {
      startDate: value => {
        if (!value) return t('required_field')
        return null
      },
      durationWeeks: value => {
        const numValue = typeof value === 'string' ? parseInt(value) : value
        if (!numValue || numValue < 1) {
          return t('new_project_page.ai_planning_min_weeks')
        }
        if (numValue > 52) {
          return t('new_project_page.ai_planning_max_weeks')
        }
        return null
      },
    },
  })


  const handleGenerate = useCallback(async () => {
    const validation = form.validate()

    if (validation.hasErrors) {
      notifications.show({
        message: t('fill_the_required_fields'),
        color: 'red',
        icon: <IconX size={24} />,
      })
      return
    }

    if (!projectId) {
      notifications.show({
        message: t('new_project_page.ai_planning_no_project'),
        color: 'red',
        icon: <IconX size={24} />,
      })
      return
    }

    if (meetingsCount > 0) {
      setConfirmModalOpened(true)
      return
    }

    await executeGeneration()
  }, [form, projectId, meetingsCount, t])

  const executeGeneration = async () => {
    if (!projectId) return

    const durationWeeks = typeof form.values.durationWeeks === 'string'
      ? parseInt(form.values.durationWeeks)
      : form.values.durationWeeks

    const result = await generate({
      projectId,
      startDate: dayjs(form.values.startDate).format('YYYY-MM-DD'),
      durationWeeks,
    })

    if (result.success) {
      notifications.show({
        title: t('new_project_page.ai_planning_success_title'),
        message: t('new_project_page.ai_planning_success_message', {
          meetings: result.meetingsCreated,
          activities: result.activitiesCreated,
        }),
        color: 'green',
        icon: <IconCheck size={24} />,
      })

      onGenerate?.({
        numberOfLessons: durationWeeks,
        startDate: form.values.startDate as Date,
      })

      form.reset()
      setMeetingsCount(result.meetingsCreated)
      onClose()
    } else {
      notifications.show({
        title: t('new_project_page.ai_planning_error_title'),
        message: result.error || t('new_project_page.ai_planning_error_message'),
        color: 'red',
        icon: <IconX size={24} />,
      })
    }
  }

  const handleConfirmRegenerate = async () => {
    if (!projectId) return

    setConfirmModalOpened(false)
    setIsDeleting(true)

    try {
      const deletionResult = await deleteAllMeetings(projectId)

      if (!deletionResult.success) {
        notifications.show({
          title: t('new_project_page.deletion_error_title'),
          message: t('new_project_page.deletion_error_message'),
          color: 'red',
          icon: <IconX size={24} />,
        })
        setIsDeleting(false)
        return
      }

      setMeetingsCount(0)

      await new Promise(resolve => setTimeout(resolve, 800))

      setIsDeleting(false)

      await executeGeneration()

      onGenerate?.({
        numberOfLessons: typeof form.values.durationWeeks === 'string'
          ? parseInt(form.values.durationWeeks)
          : form.values.durationWeeks,
        startDate: form.values.startDate as Date,
      })
    } catch (error) {
      notifications.show({
        title: t('new_project_page.deletion_error_title'),
        message: t('new_project_page.deletion_error_message'),
        color: 'red',
        icon: <IconX size={24} />,
      })
      setIsDeleting(false)
    }
  }

  const handleClose = useCallback(() => {
    if (!isGenerating && !isDeleting) {
      form.reset()
      onClose()
    }
  }, [isGenerating, isDeleting, onClose, form])

  const handleEditAIDetails = () => {
    handleClose()
    setActive(0) // Volta para Step 1
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      title={
        <Group spacing='xs'>
          <IconSparkles size={24} style={{ color: '#8b5cf6' }} />
          <Text size='lg' weight={600}>
            {t('new_project_page.ai_lessons_modal_title')}
          </Text>
        </Group>
      }
      size='lg'
      closeOnClickOutside={!isGenerating && !isDeleting}
      closeOnEscape={!isGenerating && !isDeleting}
    >
      <Stack spacing='lg'>
        {isGenerating || isDeleting ? (
          <Box style={{ position: 'relative', minHeight: '400px' }}>
            <AILoadingState
              message={
                isDeleting
                  ? t('new_project_page.deleting_meetings')
                  : progress.message || t('new_project_page.ai_processing')
              }
              progress={isDeleting ? 50 : progress.percentage}
            />
          </Box>
        ) : (
          <>
            <Group position='apart' align='center'>
              <Text size='md' weight={700}>
                {t('new_project_page.want_different_suggestions')}
              </Text>
              <Button
                variant='light'
                color='grape'
                size='sm'
                leftIcon={<IconSparkles size={16} />}
                onClick={handleEditAIDetails}
              >
                {t('new_project_page.edit_ai_details')}
              </Button>
            </Group>

            <Box>
              <Text size='lg' weight={700} mb='md'>
                {t('new_project_page.duration')}
              </Text>

              <Group grow spacing='md'>
                <TextInput
                  size='md'
                  label={t('new_project_page.number_of_lessons')}
                  placeholder='0'
                  type='number'
                  inputMode='numeric'
                  required
                  withAsterisk
                  {...form.getInputProps('durationWeeks')}
                />

                <DateInput
                  size='md'
                  label={t('new_project_page.start_date')}
                  placeholder={t('new_project_page.select_date')}
                  required
                  withAsterisk
                  clearable={false}
                  valueFormat={dateValueFormat}
                  {...form.getInputProps('startDate')}
                />
              </Group>
            </Box>

            <Group position='right' spacing='sm' mt='xl'>
              <Button
                variant='outline'
                onClick={handleClose}
                size='md'
              >
                {t('new_project_page.cancel')}
              </Button>
              <Button
                onClick={handleGenerate}
                color='teal'
                size='md'
                leftIcon={<IconSparkles size={20} />}
              >
                {t('new_project_page.generate_and_apply')}
              </Button>
            </Group>
          </>
        )}
      </Stack>

      <RegenerateMeetingsConfirmModal
        opened={confirmModalOpened}
        onClose={() => setConfirmModalOpened(false)}
        onConfirm={handleConfirmRegenerate}
        meetingsCount={meetingsCount}
      />
    </Modal>
  )
}
